import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    IconButton,
    Paper,
    Divider,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Card,
    CardContent,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Alert,
    AlertTitle,
    Fab,
} from "@mui/material";
import {
    Plus,
    Trash2,
    Calendar,
    BookOpen,
    Clock,
    User,
    Users,
    ChevronLeft,
    Save,
    PlusCircle,
    X,
    ChevronDown,
    Image as ImageIcon,
    UploadCloud,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import CourseCardAI from "../../../../Assets/Images/CourseCardAI.png";


interface Topic {
    id: string;
    name: string;
    duration: string;
    subtopics: string[];
}

interface Subject {
    id: string;
    yearGroup: string;
    name: string;
    topics: Topic[];
    startDate: string;
    endDate: string;
    references: {
        studyNotes: string;
        booklet: string;
        videoTutorials: string;
    };
}

const CreateCourse = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const courseId = searchParams.get("id");

    const [courseName, setCourseName] = useState("");
    const [shortName, setShortName] = useState("");
    const [courseImage, setCourseImage] = useState<File | null>(null);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [courseDescription, setCourseDescription] = useState("");
    const [category, setCategory] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [subjects, setSubjects] = useState<Subject[]>([]);

    useEffect(() => {
        if (courseId) {
            const stored = JSON.parse(localStorage.getItem("tutoring_courses") || "[]");
            const courseToEdit = stored.find((c: any) => String(c.id) === String(courseId));
            if (courseToEdit) {
                setCourseName(courseToEdit.title || "");
                setShortName(courseToEdit.shortName || "");
                setPreviewImage(courseToEdit.image || null);
                setCourseDescription(courseToEdit.description || "");
                setCategory(courseToEdit.category || "");
                setStartDate(courseToEdit.startDate || "");
                setEndDate(courseToEdit.endDate || "");
                setSubjects(courseToEdit.subjects || []);
            }
        }
    }, [courseId]);

    const categories = [
        "Information Technology",
        "Mathematics & Logic",
        "Science & Engineering",
        "Business & Management",
        "Arts & Humanities",
        "Health & Medicine",
        "Social Sciences",
        "Language & Literature",
        "Competitive Exams",
        "Creative Arts",
        "Secondary Education"
    ];

    const sampleTopics = ["Introduction", "Fundamentals", "Advanced Concepts", "Practicals", "Case Studies", "Final Review", "Quiz", "Assignment"];
    
    const sampleYearGroups = ["Year 7", "Year 8", "Year 9", "Year 10", "Year 11", "Year 12", "Year 13"];

    const sampleSubjects = [
        "Mathematics", 
        "Physics", 
        "Chemistry", 
        "Biology", 
        "Computer Science", 
        "English Literature", 
        "History", 
        "Business Studies", 
        "Economics", 
        "Psychology"
    ];

    const sampleStudyNotes = ["Introductory Notes", "Advanced Formulas", "Exam Revision Guide", "Summary Sheets", "Practical Handbook"];
    const sampleBooklets = ["Module 1 Booklet", "Practice Workbook", "Problem Set Vol 1", "Laboratory Manual", "Previous Year Questions"];
    const sampleVideos = ["Full Lecture Video", "Concept Summary", "Problem Solving Tutorial", "Live Session Recording", "Quick Tips"];

    const addSubject = () => {
        setSubjects([{
            id: Date.now().toString(),
            yearGroup: "",
            name: "",
            topics: [],
            startDate: "",
            endDate: "",
            references: {
                studyNotes: "",
                booklet: "",
                videoTutorials: "",
            }
        }, ...subjects]);
    };

    const removeSubject = (id: string) => {
        setSubjects(subjects.filter(s => s.id !== id));
    };

    const handleSubjectChange = (id: string, field: keyof Subject, value: any) => {
        setSubjects(subjects.map(s => s.id === id ? { ...s, [field]: value } : s));
    };

    const addTopic = (subjectId: string) => {
        setSubjects(subjects.map(s => {
            if (s.id === subjectId) {
                return {
                    ...s,
                    topics: [...s.topics, { id: Date.now().toString(), name: "", duration: "", subtopics: [] }]
                };
            }
            return s;
        }));
    };

    const handleTopicSelection = (subjectId: string, selectedNames: string[]) => {
        setSubjects(prev => prev.map(s => {
            if (s.id === subjectId) {
                // Keep existing topics that are still selected
                const existingTopics = s.topics.filter(t => selectedNames.includes(t.name));
                // Find new names that don't have a card yet
                const newNames = selectedNames.filter(name => !s.topics.find(t => t.name === name));
                // Create new cards for new names
                const newTopics = newNames.map(name => ({
                    id: name + Date.now(), // Use name + timestamp for uniqueness
                    name,
                    duration: "",
                    subtopics: []
                }));
                return { ...s, topics: [...existingTopics, ...newTopics] };
            }
            return s;
        }));
    };

    const removeTopic = (subjectId: string, topicId: string) => {
        setSubjects(subjects.map(s => {
            if (s.id === subjectId) {
                return {
                    ...s,
                    topics: s.topics.filter(t => t.id !== topicId)
                };
            }
            return s;
        }));
    };

    const handleTopicChange = (subjectId: string, topicId: string, field: keyof Topic, value: any) => {
        setSubjects(subjects.map(s => {
            if (s.id === subjectId) {
                return {
                    ...s,
                    topics: s.topics.map(t => t.id === topicId ? { ...t, [field]: value } : t)
                };
            }
            return s;
        }));
    };

    const addSubtopic = (subjectId: string, topicId: string) => {
        setSubjects(subjects.map(s => {
            if (s.id === subjectId) {
                return {
                    ...s,
                    topics: s.topics.map(t => t.id === topicId ? { ...t, subtopics: [...t.subtopics, ""] } : t)
                };
            }
            return s;
        }));
    };

    const handleSubtopicChange = (subjectId: string, topicId: string, index: number, value: string) => {
        setSubjects(subjects.map(s => {
            if (s.id === subjectId) {
                return {
                    ...s,
                    topics: s.topics.map(t => {
                        if (t.id === topicId) {
                            const newSubtopics = [...t.subtopics];
                            newSubtopics[index] = value;
                            return { ...t, subtopics: newSubtopics };
                        }
                        return t;
                    })
                };
            }
            return s;
        }));
    };



    const handleSubmit = () => {
        const freshCourse = {
            id: courseId ? Number(courseId) : Date.now(),
            title: courseName,
            shortName: shortName,
            category: category || "General",
            lessons: subjects.length,
            duration: `${totalCourseDays} days`,
            rating: 5.0,
            students: courseId ? Number(searchParams.get("students") || 0) : 0,
            progress: courseId ? Number(searchParams.get("progress") || 0) : 0,
            image: previewImage || CourseCardAI,
            status: 1,
            startDate,
            endDate,
            description: courseDescription,
            subjects
        };
        
        const existingCourses = JSON.parse(localStorage.getItem("tutoring_courses") || "[]");
        
        let updatedCourses;
        if (courseId) {
            updatedCourses = existingCourses.map((c: any) => String(c.id) === String(courseId) ? freshCourse : c);
        } else {
            updatedCourses = [...existingCourses, freshCourse];
        }
        
        localStorage.setItem("tutoring_courses", JSON.stringify(updatedCourses));
        navigate("/online-tutoring/courses");
    };

    const totalCourseDays = startDate && endDate ? Math.floor((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 3600 * 24)) + 1 : 0;
    
    // Calculate scheduled days (unique dates covered by subjects)
    const getScheduledDaysCount = () => {
        const scheduledDates = new Set();
        subjects.forEach(s => {
            if (s.startDate && s.endDate) {
                let current = new Date(s.startDate);
                const end = new Date(s.endDate);
                while (current <= end) {
                    scheduledDates.add(current.toISOString().split('T')[0]);
                    current.setDate(current.getDate() + 1);
                }
            }
        });
        return scheduledDates.size;
    };

    const scheduledDays = getScheduledDaysCount();

    return (
        <Box sx={{ minHeight: "100vh" }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <IconButton 
                        onClick={() => navigate("/online-tutoring/courses")} 
                        size="small" 
                        sx={{ 
                            bgcolor: "#fff", 
                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                            "&:hover": { bgcolor: "#f1f5f9" }
                        }}
                    >
                        <ChevronLeft size={20} />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>
                        {courseId ? "Edit Course" : "Create New Course"}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{  mx: "auto" }}>
                {startDate && endDate && scheduledDays < totalCourseDays && (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: "12px", border: "1px solid #009dff20" }}>
                        <AlertTitle sx={{ fontWeight: 700 }}>Schedule in Progress</AlertTitle>
                        Course span is <strong>{totalCourseDays} days</strong>. You have scheduled subjects for <strong>{scheduledDays} days</strong>. Please ensure all days are covered.
                    </Alert>
                )}
                <AnimatePresence>
                    {/* Basic Information Section */}
                    <Accordion defaultExpanded sx={{ mb: 2, borderRadius: "16px !important", border: "1px solid #e2e8f0", boxShadow: "0 4px 24px rgba(0,0,0,0.02)", "&:before": { display: "none" } }}>
                        <AccordionSummary expandIcon={<ChevronDown size={20} />} sx={{ px: 3, py: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.5, color: "#1e293b" }}>
                                <Box sx={{ p: 1, bgcolor: "#e0f2fe", borderRadius: "10px", display: "flex" }}>
                                    <BookOpen size={20} color="#0284c7" />
                                </Box>
                                Basic Information
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 3, pb: 4 }}>
                            <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Course Name"
                                    placeholder="e.g. Advanced Mathematics 2024"
                                    value={courseName}
                                    onChange={(e) => setCourseName(e.target.value)}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#f8fafc" } }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Short Name"
                                    placeholder="e.g. Adv Math"
                                    value={shortName}
                                    onChange={(e) => setShortName(e.target.value)}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#f8fafc" } }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth required>
                                    <InputLabel>Category</InputLabel>
                                    <Select
                                        label="Category"
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        sx={{ borderRadius: "12px", bgcolor: "#f8fafc" }}
                                    >
                                        {categories.map(cat => (
                                            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    multiline
                                    rows={4}
                                    value={courseDescription}
                                    onChange={(e) => setCourseDescription(e.target.value)}
                                    placeholder="Describe what this course covers..."
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#f8fafc" } }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: "flex", alignItems: "center", gap: 1, color: "#475569" }}>Course Image</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, p: 3, border: "2px dashed #e2e8f0", borderRadius: "16px", bgcolor: "#f8fafc" }}>
                                        {previewImage ? (
                                            <Box
                                                component="img"
                                                src={previewImage}
                                                alt="Preview"
                                                sx={{ height: 80, width: 80, borderRadius: "12px", objectFit: "cover", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                                            />
                                        ) : (
                                            <Box sx={{ height: 80, width: 80, borderRadius: "12px", bgcolor: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <ImageIcon size={32} color="#94a3b8" />
                                            </Box>
                                        )}
                                        <Box>
                                            <Button
                                                variant="contained"
                                                component="label"
                                                startIcon={<UploadCloud size={18} />}
                                                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: "#ffffff", bgcolor: "#009dff", "&:hover": { bgcolor: "#0f172a" }, boxShadow: "none" }}
                                            >
                                                Upload New Image
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                            setCourseImage(file);
                                                            const reader = new FileReader();
                                                            reader.onloadend = () => {
                                                                setPreviewImage(reader.result as string);
                                                            };
                                                            reader.readAsDataURL(file);
                                                        }
                                                    }}
                                                />
                                            </Button>
                                            <Typography variant="caption" display="block" sx={{ mt: 1, color: "#64748b" }}>
                                                Recommended size: 800x600px. JPG, PNG or WebP.
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Course Schedule Section */}
                    <Accordion defaultExpanded sx={{ mb: 2, borderRadius: "16px !important", border: "1px solid #e2e8f0", boxShadow: "0 4px 24px rgba(0,0,0,0.02)", "&:before": { display: "none" } }}>
                        <AccordionSummary expandIcon={<ChevronDown size={20} />} sx={{ px: 3, py: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.5, color: "#1e293b" }}>
                                <Box sx={{ p: 1, bgcolor: "#fef3c7", borderRadius: "10px", display: "flex" }}>
                                    <Calendar size={20} color="#d97706" />
                                </Box>
                                Overall Schedule
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 3, pb: 4 }}>
                            <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Course Start Date"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#f8fafc" } }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Course End Date"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#f8fafc" } }}
                                />
                            </Grid>
                        </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Subjects & Curriculum Section */}
                    <Accordion defaultExpanded sx={{ mb: 2, borderRadius: "16px !important", border: "1px solid #e2e8f0", boxShadow: "0 4px 24px rgba(0,0,0,0.02)", "&:before": { display: "none" } }}>
                        <AccordionSummary expandIcon={<ChevronDown size={20} />} sx={{ px: 3, py: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.5, color: "#1e293b" }}>
                                    <Box sx={{ p: 1, bgcolor: "#dcfce7", borderRadius: "10px", display: "flex" }}>
                                        <Plus size={20} color="#16a34a" />
                                    </Box>
                                    Subjects 
                                    <Chip label={subjects.length.toString()} size="small" sx={{ fontWeight: 700, bgcolor: "#f1f5f9", color: "#475569", borderRadius: "8px" }} />
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<Plus size={18} />}
                                    onClick={(e) => { e.stopPropagation(); addSubject(); }}
                                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", color: "#16a34a", borderColor: "#16a34a", "&:hover": { bgcolor: "#f0fdf4", borderColor: "#16a34a" } }}
                                >
                                    Add Subject
                                </Button>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 3, pb: 4 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {subjects.map((subject, index) => (
                                <Accordion key={subject.id} defaultExpanded sx={{ borderRadius: "12px !important", border: "1px solid #e2e8f0", boxShadow: "none", "&:before": { display: "none" } }}>
                                    <AccordionSummary expandIcon={<ChevronDown size={20} />} sx={{ bgcolor: "#f8fafc", borderBottom: "1px solid #f1f5f9", borderRadius: "12px", "&.Mui-expanded": { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 2 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1.5, color: "#334155" }}>
                                                <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#fff", color: "#0ea5e9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", fontWeight: 800, border: "1px solid #e2e8f0", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
                                                    {subjects.length - index}
                                                </Box>
                                                {subject.name || `New Subject`}
                                            </Typography>
                                            <Tooltip title="Remove Subject">
                                                <IconButton onClick={(e) => { e.stopPropagation(); removeSubject(subject.id); }} sx={{ color: "#ef4444", bgcolor: "#fee2e2", "&:hover": { bgcolor: "#fecaca" }, borderRadius: "8px", width: 36, height: 36 }}>
                                                    <Trash2 size={16} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 3 }}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={2}>
                                                <FormControl fullWidth required>
                                                    <InputLabel>Year Group</InputLabel>
                                                    <Select
                                                        label="Year Group"
                                                        value={subject.yearGroup || ""}
                                                        onChange={(e) => handleSubjectChange(subject.id, "yearGroup", e.target.value)}
                                                        sx={{ borderRadius: "12px" }}
                                                    >
                                                        {sampleYearGroups.map(yg => (
                                                            <MenuItem key={yg} value={yg}>{yg}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <FormControl fullWidth required disabled={!subject.yearGroup}>
                                                    <InputLabel>Subject Name</InputLabel>
                                                    <Select
                                                        label="Subject Name"
                                                        value={subject.name}
                                                        onChange={(e) => handleSubjectChange(subject.id, "name", e.target.value)}
                                                        sx={{ borderRadius: "12px" }}
                                                    >
                                                        {sampleSubjects.map(s => (
                                                            <MenuItem key={s} value={s}>{s}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <FormControl fullWidth required>
                                                    <InputLabel>Topics to Cover</InputLabel>
                                                    <Select
                                                        multiple
                                                        value={subject.topics.map(t => t.name).filter(n => n !== "")}
                                                        onChange={(e) => handleTopicSelection(subject.id, e.target.value as string[])}
                                                        label="Topics to Cover"
                                                        renderValue={(selected) => (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {(selected as string[]).map((value) => (
                                                                    <Chip key={value} label={value} size="small" sx={{ bgcolor: "#eff6ff", color: "#2563eb", height: 24, fontWeight: 600, borderRadius: "6px" }} />
                                                                ))}
                                                            </Box>
                                                        )}
                                                        sx={{ borderRadius: "12px" }}
                                                    >
                                                        {sampleTopics.map((t) => (
                                                            <MenuItem key={t} value={t}>{t}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={2}>
                                                <TextField
                                                    fullWidth
                                                    required
                                                    label="Start Date"
                                                    type="date"
                                                    value={subject.startDate}
                                                    onChange={(e) => handleSubjectChange(subject.id, "startDate", e.target.value)}
                                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{ min: startDate, max: endDate }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={2}>
                                                <TextField
                                                    fullWidth
                                                    required
                                                    label="End Date"
                                                    type="date"
                                                    value={subject.endDate}
                                                    onChange={(e) => handleSubjectChange(subject.id, "endDate", e.target.value)}
                                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{ min: subject.startDate || startDate, max: endDate }}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 1, borderStyle: "dashed", borderColor: "#e2e8f0" }} />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1, color: "#475569" }}>
                                                 Linked References & Materials
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={4}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>Study Notes</InputLabel>
                                                            <Select
                                                                label="Study Notes"
                                                                value={subject.references.studyNotes}
                                                                onChange={(e) => handleSubjectChange(subject.id, "references", { ...subject.references, studyNotes: e.target.value })}
                                                                sx={{ borderRadius: "12px", bgcolor: "#f8fafc" }}
                                                            >
                                                                {sampleStudyNotes.map(n => (
                                                                    <MenuItem key={n} value={n}>{n}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>Booklet</InputLabel>
                                                            <Select
                                                                label="Booklet"
                                                                value={subject.references.booklet}
                                                                onChange={(e) => handleSubjectChange(subject.id, "references", { ...subject.references, booklet: e.target.value })}
                                                                sx={{ borderRadius: "12px", bgcolor: "#f8fafc" }}
                                                            >
                                                                {sampleBooklets.map(b => (
                                                                    <MenuItem key={b} value={b}>{b}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>Video Lessons</InputLabel>
                                                            <Select
                                                                label="Video Lessons"
                                                                value={subject.references.videoTutorials}
                                                                onChange={(e) => handleSubjectChange(subject.id, "references", { ...subject.references, videoTutorials: e.target.value })}
                                                                sx={{ borderRadius: "12px", bgcolor: "#f8fafc" }}
                                                            >
                                                                {sampleVideos.map(v => (
                                                                    <MenuItem key={v} value={v}>{v}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                        </AccordionDetails>
                    </Accordion>
                </AnimatePresence>

                {/* Floating Save Button */}
                <Fab 
                    color="primary" 
                    aria-label="save" 
                    onClick={handleSubmit} 
                    disabled={scheduledDays < totalCourseDays || totalCourseDays === 0}
                    sx={{ 
                        position: 'fixed', 
                        bottom: 32, 
                        right: 32,
                        zIndex: 1000,
                        width: 64,
                        height: 64,
                        background: (scheduledDays < totalCourseDays || totalCourseDays === 0) ? "#cbd5e1" : "linear-gradient(135deg, #009dff 0%, #7c3aed 100%)",
                        boxShadow: (scheduledDays < totalCourseDays || totalCourseDays === 0) ? "none" : "0 8px 24px rgba(79, 70, 229, 0.4)",
                        "&:hover": {
                            background: "linear-gradient(135deg, #009dff 0%, #6d28d9 100%)",
                            boxShadow: "0 12px 28px rgba(79, 70, 229, 0.6)"
                        },
                        "&.Mui-disabled": {
                            background: "#e2e8f0",
                            color: "#94a3b8"
                        }
                    }}
                >
                    <Save size={28} color={(scheduledDays < totalCourseDays || totalCourseDays === 0) ? "#94a3b8" : "#fff"} />
                </Fab>
                {scheduledDays < totalCourseDays && totalCourseDays > 0 && (
                    <Box sx={{ position: 'fixed', bottom: 44, right: 110, display: "flex", alignItems: "center", background: "#fff", p: 1.5, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 1000, border: "1px solid #fee2e2" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#ef4444" }}>
                            * You must schedule {totalCourseDays - scheduledDays} more days before saving
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default CreateCourse;
